import PropTypes from "prop-types";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { useSelector, useDispatch } from "react-redux";
import { selectMenus } from "../../../../redux/editorSlice";
import { MENU_TYPES } from "../../../../constants";
import { logoutUser } from "../../../../redux/WithcookieSlice";

const NavMenu = ({ menuWhiteClass, sidebarMenu }) => {
  const { t } = useTranslation();
  const menus = useSelector(selectMenus);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // 주 메뉴만 필터링 (표시 가능한 메뉴만) - 글로벌 메뉴는 표시하지 않음
  const mainMenus = menus.filter(
    menu => menu.type === MENU_TYPES.MAIN && 
    menu.visible !== false && 
    !menu.parentId // 최상위 메뉴만
  ).sort((a, b) => a.order - b.order);
  
  // 메뉴 자식 항목 찾기 함수 - 글로벌 메뉴의 자식도 제외
  const getChildMenus = (parentId) => {
    return menus
      .filter(menu => 
        menu.parentId === parentId && 
        menu.visible !== false &&
        menu.type === MENU_TYPES.MAIN // 글로벌 메뉴의 자식은 표시하지 않음
      )
      .sort((a, b) => a.order - b.order);
  };
  
  // 메뉴 클릭 핸들러
  const handleMenuClick = (e, menu) => {
    e.preventDefault();
    
    // 메뉴 액션 처리
    if (menu.menuAction === 'logout') {
      dispatch(logoutUser()).then(() => {
        // 로그아웃 성공 후 홈 페이지로 이동
        navigate('/');
      });
      return;
    }
    
    // 외부 링크 처리
    if (menu.externalLink) {
      window.open(menu.externalLink, '_blank');
      return;
    }
    
    // 일반 내부 링크 처리
    navigate(menu.path || '/');
  };
  
  // 메뉴 렌더링 함수
  const renderMenuItems = () => {
    // "/" 경로를 가진 홈 메뉴가 있는지 확인
    const hasHomeMenu = mainMenus.some(menu => menu.path === '/');
    
    if (mainMenus.length === 0 || !hasHomeMenu) {
      // 주 메뉴가 없거나 홈 메뉴가 없는 경우에만 기본 홈 메뉴 표시
      return (
        <li>
          <Link to="/">
            홈
            {!sidebarMenu && <i className="fa fa-angle-down" />}
          </Link>
        </li>
      );
    }
    
    return mainMenus.map(menu => {
      const childMenus = getChildMenus(menu.id);
      const hasChildren = childMenus.length > 0;
      
      return (
        <li key={menu.id} className={hasChildren ? "menu-item-has-children" : ""}>
          {menu.externalLink || menu.menuAction ? (
            // 외부 링크나 특수 액션이 있는 메뉴
            <a href="#" onClick={(e) => handleMenuClick(e, menu)}>
              {menu.name}
              {hasChildren && (
                sidebarMenu ? (
                  <span>
                    <i className="fa fa-angle-right"></i>
                  </span>
                ) : (
                  <i className="fa fa-angle-down" />
                )
              )}
            </a>
          ) : (
            // 일반 내부 링크 메뉴
            <Link to={menu.path || "/"}>
              {menu.name}
              {hasChildren && (
                sidebarMenu ? (
                  <span>
                    <i className="fa fa-angle-right"></i>
                  </span>
                ) : (
                  <i className="fa fa-angle-down" />
                )
              )}
            </Link>
          )}
          
          {hasChildren && (
            <ul className={childMenus.length > 3 ? "mega-menu mega-menu-padding" : "submenu"}>
              {childMenus.map(childMenu => {
                const grandChildMenus = getChildMenus(childMenu.id);
                const hasGrandChildren = grandChildMenus.length > 0;
                
                return (
                  <li key={childMenu.id} className={hasGrandChildren ? "menu-item-has-children" : ""}>
                    {childMenu.externalLink || childMenu.menuAction ? (
                      // 외부 링크나 특수 액션이 있는 서브메뉴
                      <a href="#" onClick={(e) => handleMenuClick(e, childMenu)}>
                        {childMenu.name}
                        {hasGrandChildren && !sidebarMenu && (
                          <i className="fa fa-angle-right"></i>
                        )}
                      </a>
                    ) : (
                      // 일반 내부 링크 서브메뉴
                      <Link to={childMenu.path || "/"}>
                        {childMenu.name}
                        {hasGrandChildren && !sidebarMenu && (
                          <i className="fa fa-angle-right"></i>
                        )}
                      </Link>
                    )}
                    
                    {hasGrandChildren && (
                      <ul className="submenu">
                        {grandChildMenus.map(grandChild => {
                          const greatGrandChildMenus = getChildMenus(grandChild.id);
                          const hasGreatGrandChildren = greatGrandChildMenus.length > 0;
                          
                          return (
                            <li key={grandChild.id} className={hasGreatGrandChildren ? "menu-item-has-children" : ""}>
                              {grandChild.externalLink || grandChild.menuAction ? (
                                // 외부 링크나 특수 액션이 있는 서브메뉴
                                <a href="#" onClick={(e) => handleMenuClick(e, grandChild)}>
                                  {grandChild.name}
                                  {hasGreatGrandChildren && !sidebarMenu && (
                                    <i className="fa fa-angle-right"></i>
                                  )}
                                </a>
                              ) : (
                                // 일반 내부 링크 서브메뉴
                                <Link to={grandChild.path || "/"}>
                                  {grandChild.name}
                                  {hasGreatGrandChildren && !sidebarMenu && (
                                    <i className="fa fa-angle-right"></i>
                                  )}
                                </Link>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </li>
      );
    });
  };

  return (
    <div className={clsx("main-menu", menuWhiteClass ? menuWhiteClass : "")}>
      <nav>
        <ul>
          {renderMenuItems()}
        </ul>
      </nav>
    </div>
  );
};

NavMenu.propTypes = {
  menuWhiteClass: PropTypes.string,
  sidebarMenu: PropTypes.bool
};

export default NavMenu;
